import { useData } from "@/contexts/DataContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Bell,
  Package,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Smartphone,
  MoreVertical,
  ExternalLink,
  BellRing
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter"; // FIXED: Added this missing import
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

const LOW_STOCK_THRESHOLD = 10;

export default function Alerts() {
  const { toast } = useToast();
  const {
    products,
    userMode,
    readNotificationIds,
    updateNotificationReadIds,
    dismissedNotificationIds,
    dismissNotificationIds
  } = useData();

  const [isNotifDenied, setIsNotifDenied] = useState(false);
  const [hideDeniedCard, setHideDeniedCard] = useState(false);

  useEffect(() => {
    const checkDeniedStatus = async () => {
      const dismissedDenied = localStorage.getItem("denied_notif_card_dismissed");

      if (dismissedDenied) {
        setHideDeniedCard(true);
        return;
      }

      if (typeof Notification !== "undefined" && Notification.permission === 'denied') {
        setIsNotifDenied(true);
        return;
      }

      try {
        const { Capacitor } = await import('@capacitor/core');

        if (Capacitor.isNativePlatform()) {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const status = await PushNotifications.checkPermissions();

          if (status.receive === 'denied' || status.receive === 'prompt-with-rationale') {
            setIsNotifDenied(true);
          }
        }
      } catch (e) {
        console.error("Failed to check push permissions", e);
      }
    };

    checkDeniedStatus();
  }, []);

  const handleDismissDeniedCard = () => {
    localStorage.setItem("denied_notif_card_dismissed", "true");
    setHideDeniedCard(true);
  };

  const handleGrantAccess = async () => {
    try {
      const { Capacitor } = await import('@capacitor/core');

      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const result = await PushNotifications.requestPermissions();

        if (result.receive === 'granted') {
          setIsNotifDenied(false);
          toast({ title: "Notifications Enabled!" });
          return;
        }
      } else if (typeof Notification !== "undefined") {
        const result = await Notification.requestPermission();

        if (result === 'granted') {
          setIsNotifDenied(false);
          return;
        }
      }
    } catch (e) {
      console.error(e);
    }

    toast({
      title: "Action Required",
      description: "Please go to your phone's Settings > Apps > DailyHisaab > Permissions to enable Notifications.",
      duration: 6000,
    });
  };

  const readIds = useMemo(() => new Set<string>(readNotificationIds), [readNotificationIds]);
  const dismissedIds = useMemo(() => new Set<string>(dismissedNotificationIds), [dismissedNotificationIds]);

  const notifications = useMemo(() => {
    if (userMode !== "pro") return [];

    const list: {
      id: string;
      type: "outofstock" | "lowstock";
      title: string;
      message: string;
      priority: "critical" | "high";
      data: any;
    }[] = [];

    products.forEach((p) => {
      if (p.stock === undefined) return;

      // Correctly flags negative stock (e.g., -2)
      if (p.stock <= 0) {
        const id = `oos-${p.id}-${p.stock}`;
        if (!dismissedIds.has(id)) {
          list.push({
            id,
            type: "outofstock",
            title: "Out of Stock",
            message: `${p.name} is out of stock. Restock immediately to avoid missed sales.`,
            priority: "critical",
            data: p
          });
        }
      } else if (p.lowStockAlert && p.lowStockAlert > 0 && p.stock <= p.lowStockAlert) {
        const id = `low-${p.id}-${p.stock}`;
        if (!dismissedIds.has(id)) {
          list.push({
            id,
            type: "lowstock",
            title: "Low Stock Alert",
            message: `${p.name} has only ${p.stock} units left.`,
            priority: "high",
            data: p
          });
        }
      }
    });

    return list.sort((a, b) => {
      if (a.priority === "critical" && b.priority !== "critical") return -1;
      if (b.priority === "critical" && a.priority !== "critical") return 1;
      return 0;
    });
  }, [products, userMode, dismissedIds]);

  const unreadNotifications = notifications.filter((n) => !readIds.has(n.id));
  const readNotifications = notifications.filter((n) => readIds.has(n.id));

  const markAsRead = (id: string) => {
    if (!readIds.has(id)) {
      updateNotificationReadIds([...readNotificationIds, id]);
    }
  };

  const markAllRead = () => {
    const next = new Set<string>(readNotificationIds);
    notifications.forEach((n) => next.add(n.id));
    updateNotificationReadIds(Array.from(next));
  };

  const clearAllRead = async () => {
    await dismissNotificationIds(readNotificationIds);
    await updateNotificationReadIds([]);
  };

  return (
    <div className="pb-20 bg-[#FDFCF7] dark:bg-[#1A1614] min-h-screen">
      {/* Fixed Sticky Header optimized for mobile spacing */}
      <div className="sticky top-0 z-30 bg-[#FDFCF7]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b">
        <div className="px-4 py-3 flex items-center justify-between gap-2">

          <div className="flex items-center gap-2 min-w-0">
            <Logo size="md" />
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white truncate">
              Alerts
            </h1>
          </div>

          {userMode === "pro" && unreadNotifications.length > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-full text-[10px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 shrink-0 active:scale-95 transition-transform"
            >
              <CheckCircle2 className="h-3 w-3" />
              <span>Mark Read</span>
            </button>
          )}

        </div>
      </div>

      <div className="p-4 space-y-6">

        {/* 1. Critical Actions Section */}
        <AnimatePresence>
          {isNotifDenied && !hideDeniedCard && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-2"
            >
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 flex items-center px-1">
                Critical Actions
                <span className="bg-[#8B1A1A] text-white px-2 py-0.5 rounded-full ml-2 text-[10px]">1</span>
              </p>

              <div className="bg-white dark:bg-slate-900 rounded-[24px] p-5 shadow-sm border border-slate-100 dark:border-slate-800 relative flex gap-4 items-start">

                <DropdownMenu>
                  <DropdownMenuTrigger className="absolute top-4 right-4 text-slate-400 p-1 rounded-full outline-none">
                    <MoreVertical className="w-5 h-5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleDismissDeniedCard} className="text-red-600 font-medium">
                      Dismiss forever
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="w-12 h-12 rounded-[14px] bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 flex items-center justify-center flex-shrink-0">
                  <BellRing className="w-6 h-6 text-[#8B1A1A] stroke-[1.5]" />
                </div>

                <div className="pr-6">
                  <h3 className="font-bold text-[15px] text-slate-900 dark:text-white mb-1.5 leading-tight">
                    Grant notifications access
                  </h3>
                  <p className="text-[13px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                    Never miss an important stock alert or payment update again.
                  </p>
                  <button
                    onClick={handleGrantAccess}
                    className="text-[#8B1A1A] font-bold text-sm flex items-center gap-1.5 active:opacity-70"
                  >
                    Grant Access <ExternalLink className="w-4 h-4 stroke-[2.5]" />
                  </button>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 2. Stock Alerts */}
        {userMode === "pro" ? (
          <div className="space-y-4">

            {unreadNotifications.length > 0 && (
              <>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Unread Alerts ({unreadNotifications.length})
                </p>

                {unreadNotifications.map((notif) => (
                  <Card
                    key={notif.id}
                    className={`border-l-4 cursor-pointer transition-all active:scale-[0.98] ${notif.type === "outofstock" ? "border-l-red-600 bg-red-50" : "border-l-amber-500 bg-amber-50"
                      }`}
                    onClick={() => markAsRead(notif.id)}
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className={`p-2 rounded-full flex-shrink-0 ${notif.type === "outofstock" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"
                        }`}>
                        {notif.type === "outofstock" ? <AlertTriangle className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-0.5 leading-tight">{notif.title}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{notif.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {readNotifications.length > 0 && (
              <>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mt-6">
                  Recent History
                </p>

                {readNotifications.map((notif) => (
                  <Card key={notif.id} className="border-l-4 border-l-slate-200 dark:border-l-slate-800 opacity-60">
                    <CardContent className="p-4 flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm text-slate-500 line-through leading-tight">{notif.title}</h3>
                        <p className="text-sm text-slate-400">{notif.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllRead}
                  className="w-full text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >
                  <Trash2 className="h-3 w-3 mr-2" /> Clear History
                </Button>
              </>
            )}

            {notifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center opacity-40">
                <Bell className="h-12 w-12 mb-3 text-slate-300" />
                <p className="font-black text-slate-400 uppercase tracking-widest text-xs">No active alerts</p>
                <p className="text-sm text-slate-400 mt-1">All products are well stocked.</p>
              </div>
            )}

          </div>
        ) : (
          /* 3. Normal Mode Upgrade Prompt */
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-amber-50/50 dark:bg-amber-900/10 rounded-[32px] border border-amber-100 dark:border-amber-900/50 border-dashed">
            <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm mb-4">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <p className="font-black text-slate-900 dark:text-white uppercase tracking-tight">Stock Alerts require Pro</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">Upgrade to Pro to get real-time low stock and out-of-stock notifications.</p>
            <Link href="/settings" className="mt-6">
              <Button className="bg-[#8B1A1A] hover:bg-[#631212] rounded-2xl px-8 font-black uppercase tracking-widest text-[10px] text-white">Upgrade Now</Button>
            </Link>
          </div>
        )}

      </div>
    </div>
  );
}